import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { BusinessHealthKPI, AttentionItem, GrowthMetric, TimelineEvent, NocKPI as NocKPIType, PlatformService, NocCriticalAlert as NocCriticalAlertType, QuickAction, NocActivity } from '@/mocks/superAdminDashboard';
import { saDashboardData, nocKpis, platformServices, nocCriticalAlerts, quickActions, nocActivities } from '@/mocks/superAdminDashboard';
import { SwalAlert } from '@/hooks/useSweetAlert';
import SASidebar from './components/SASidebar';
import { getTopEconomyRadios, stateConfig as economyStateConfig } from '@/mocks/radioEconomyScores';
import RadioEconomyScore from '@/components/feature/RadioEconomyScore';
import { radioEconomyScores } from '@/mocks/radioEconomyScores';
import FrequencyStatusBanner from '@/components/feature/FrequencyStatusBanner';
import NocKpiCard from './components/NocKpiCard';
import PlatformHealthPanel from './components/PlatformHealthPanel';
import NocActivityFeed from './components/NocActivityFeed';
import NocCriticalAlerts from './components/NocCriticalAlerts';
import QuickActionsPanel from './components/QuickActionsPanel';

/* ── Health card accent map ── */
const healthAccent: Record<string, { dot: string; iconBg: string; iconText: string }> = {
  primary:  { dot: 'bg-primary-500', iconBg: 'bg-primary-100', iconText: 'text-primary-600' },
  accent:   { dot: 'bg-accent-500', iconBg: 'bg-accent-100', iconText: 'text-accent-600' },
  secondary:{ dot: 'bg-secondary-500', iconBg: 'bg-secondary-100', iconText: 'text-secondary-600' },
  success:  { dot: 'bg-emerald-500', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
  warning:  { dot: 'bg-amber-500', iconBg: 'bg-amber-100', iconText: 'text-amber-600' },
  danger:   { dot: 'bg-red-500', iconBg: 'bg-red-100', iconText: 'text-red-500' },
};

const growthAccent: Record<string, { bg: string; iconBg: string; iconText: string; trendBg: string; trendText: string }> = {
  primary:  { bg: 'bg-primary-50', iconBg: 'bg-primary-100', iconText: 'text-primary-600', trendBg: 'bg-primary-100', trendText: 'text-primary-700' },
  accent:   { bg: 'bg-accent-50', iconBg: 'bg-accent-100', iconText: 'text-accent-600', trendBg: 'bg-accent-100', trendText: 'text-accent-700' },
  secondary:{ bg: 'bg-secondary-50', iconBg: 'bg-secondary-100', iconText: 'text-secondary-600', trendBg: 'bg-secondary-100', trendText: 'text-secondary-700' },
  success:  { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', trendBg: 'bg-emerald-100', trendText: 'text-emerald-700' },
};

/* ── Timeline config ── */
const timelineCfg: Record<TimelineEvent['type'], { icon: string; bg: string; color: string; label: string }> = {
  nueva_radio:      { icon: 'ri-radio-button-line', bg: 'bg-primary-100', color: 'text-primary-600', label: 'Nueva radio' },
  nuevo_pago:       { icon: 'ri-money-dollar-circle-line', bg: 'bg-emerald-100', color: 'text-emerald-600', label: 'Nuevo pago' },
  contrato_firmado: { icon: 'ri-file-text-line', bg: 'bg-accent-100', color: 'text-accent-600', label: 'Contrato firmado' },
  nuevo_programa:   { icon: 'ri-mic-line', bg: 'bg-secondary-100', color: 'text-secondary-600', label: 'Nuevo programa' },
  nuevo_evento:     { icon: 'ri-calendar-event-line', bg: 'bg-amber-100', color: 'text-amber-600', label: 'Nuevo evento' },
};

/* ════════════════════════════════════════════════
   SECTION 1 — Health KPI Card
   ════════════════════════════════════════════════ */
function HealthCard({ kpi, index }: { kpi: BusinessHealthKPI; index: number }) {
  const isPositive = kpi.trend >= 0;
  const acc = healthAccent[kpi.accent];

  return (
    <div
      className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5"
      style={{ animation: `fadeInUp 0.35s ease-out forwards`, animationDelay: `${index * 60}ms`, opacity: 0 }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${acc.iconBg} flex items-center justify-center`}>
          <i className={`${kpi.icon} ${acc.iconText} text-base`}></i>
        </div>
        <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${isPositive ? 'bg-emerald-100 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
          <i className={`${isPositive ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[10px]`}></i>
          {Math.abs(kpi.trend)}%
        </div>
      </div>
      <p className="text-foreground-950 text-[28px] md:text-[32px] font-extrabold tracking-tight leading-none mb-1">{kpi.value}</p>
      <p className="text-foreground-500 text-xs font-medium mb-0.5">{kpi.label}</p>
      <p className="text-foreground-400 text-[10px]">{kpi.trendLabel}</p>
      <div className={`mt-3 h-0.5 rounded-full w-full bg-background-200 overflow-hidden`}>
        <div className={`h-full rounded-full ${acc.dot}`} style={{ width: `${Math.min(Math.abs(kpi.trend) * 3, 100)}%` }}></div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   SECTION 2 — Attention Item Row
   ════════════════════════════════════════════════ */
function AttentionRow({ item }: { item: AttentionItem }) {
  const navigate = useNavigate();
  const isDanger = item.badgeAccent === 'danger';

  const handleResolve = async () => {
    const result = await Swal.fire({
      title: item.title,
      html: `<div style="text-align:left;font-size:13px">
        <p style="margin-bottom:6px"><strong style="font-size:14px">${item.radioName}</strong></p>
        <p style="color:#6b7280;margin-bottom:8px">${item.description}</p>
        <p style="color:#9ca3af;font-size:11px">Badge: ${item.badge}</p>
      </div>`,
      icon: isDanger ? 'error' : 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ir a resolver',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: isDanger ? '#ef4444' : '#f59e0b',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      },
      buttonsStyling: false,
    });
    if (result.isConfirmed) navigate(item.route);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 px-3 rounded-xl hover:bg-background-100 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isDanger ? 'bg-red-100' : 'bg-amber-100'}`}>
          <i className={`${isDanger ? 'ri-error-warning-fill text-red-500' : 'ri-alert-fill text-amber-500'} text-sm`}></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-foreground-800 text-xs font-semibold">{item.radioName}</span>
            <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold whitespace-nowrap ${isDanger ? 'bg-red-100 text-red-500' : 'bg-amber-100 text-amber-600'}`}>
              {item.badge}
            </span>
          </div>
          <p className="text-foreground-500 text-[11px] mt-0.5 truncate">{item.title} · {item.description}</p>
        </div>
      </div>
      <button
        onClick={handleResolve}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold cursor-pointer transition-colors whitespace-nowrap shrink-0 ${isDanger ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}
      >
        <i className="ri-tools-line text-[11px]"></i>
        Resolver
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════
   SECTION 3 — Growth Metric Card
   ════════════════════════════════════════════════ */
function GrowthCard({ metric, index }: { metric: GrowthMetric; index: number }) {
  const isPositive = metric.trend >= 0;
  const acc = growthAccent[metric.accent];

  return (
    <div
      className={`${acc.bg} border border-background-200 rounded-2xl p-4 md:p-5`}
      style={{ animation: `fadeInUp 0.35s ease-out forwards`, animationDelay: `${index * 60}ms`, opacity: 0 }}
    >
      <div className={`w-9 h-9 rounded-xl ${acc.iconBg} flex items-center justify-center mb-3`}>
        <i className={`${metric.icon} ${acc.iconText} text-sm`}></i>
      </div>
      <p className="text-foreground-950 text-2xl md:text-[26px] font-extrabold tracking-tight leading-none mb-1">{metric.value}</p>
      <p className="text-foreground-600 text-xs font-medium mb-2">{metric.label}</p>
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${acc.trendBg} ${acc.trendText}`}>
        <i className={`${isPositive ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[10px]`}></i>
        {Math.abs(metric.trend)}% {metric.trendLabel}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   SECTION 4 — Timeline Item (Facebook style)
   ════════════════════════════════════════════════ */
function TimelineItem({ event, isLast }: { event: TimelineEvent; isLast: boolean }) {
  const navigate = useNavigate();
  const cfg = timelineCfg[event.type];

  const handleClick = () => {
    if (event.type === 'nueva_radio') navigate('/sa/radios');
    else if (event.type === 'nuevo_pago') navigate('/sa/payments');
    else if (event.type === 'contrato_firmado') navigate('/sa/contracts');
    else if (event.type === 'nuevo_programa') navigate('/sa/radios');
    else if (event.type === 'nuevo_evento') navigate('/sa/radios');
  };

  return (
    <div className="flex gap-3">
      {/* Avatar column */}
      <div className="flex flex-col items-center shrink-0">
        <div className={`w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center`}>
          <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-background-200 my-1.5"></div>}
      </div>
      {/* Content */}
      <div className={`pb-5 min-w-0 flex-1 ${isLast ? '' : ''}`}>
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-foreground-800 text-xs font-semibold">{event.radioName}</span>
          <span className="text-foreground-400 text-[10px]">{event.timeRelative}</span>
        </div>
        <p className="text-foreground-600 text-[11px] leading-relaxed">
          <span className="text-foreground-500">{event.description}</span>
        </p>
        {event.amount && (
          <span className="inline-block mt-1 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-600 text-[9px] font-bold">{event.amount}</span>
        )}
        <button
          onClick={handleClick}
          className="mt-1.5 text-primary-600 text-[10px] font-semibold hover:text-primary-700 cursor-pointer transition-colors"
        >
          Ver radio
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════ */
export default function SuperAdminDashboard() {
  const { businessHealth, attentionItems, growthMetrics, timeline } = saDashboardData;
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const navigate = useNavigate();

  const displayedTimeline = timelineExpanded ? timeline : timeline.slice(0, 5);
  const hasMoreTimeline = timeline.length > 5;

  const dangerCount = attentionItems.filter((a) => a.badgeAccent === 'danger').length;

  const topEconomyRadios = useMemo(() => getTopEconomyRadios(), []);

  const today = useMemo(() => {
    return new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }, []);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-6 max-w-[1280px] mx-auto">

            {/* ── Page Header ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h1 className="text-foreground-950 font-bold text-lg md:text-xl">Panel de Control</h1>
                <p className="text-foreground-400 text-xs mt-0.5 capitalize">{today}</p>
              </div>
              {dangerCount > 0 && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  <span className="text-red-600 text-[10px] font-bold whitespace-nowrap">{dangerCount} urgente{dangerCount > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            {/* ── SA-27 Estatus y Exclusividad ── */}
            <FrequencyStatusBanner variant="default" />

            {/* ════════════════════════════════════════════════
                SECCIÓN 1 — SALUD DEL NEGOCIO
                ════════════════════════════════════════════════ */}
            <section>
              <div className="mb-4">
                <h2 className="text-foreground-950 font-semibold text-sm md:text-base">Salud del negocio</h2>
                <p className="text-foreground-400 text-[10px] md:text-xs mt-0.5">Indicadores clave · Actualizado en tiempo real</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {businessHealth.map((kpi, idx) => (
                  <HealthCard key={kpi.id} kpi={kpi} index={idx} />
                ))}
              </div>
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 1.5 — NOC · MÉTRICAS DEL CENTRO DE OPERACIONES
                ════════════════════════════════════════════════ */}
            <section>
              <div className="mb-4">
                <h2 className="text-foreground-950 font-semibold text-sm md:text-base">
                  <i className="ri-radar-line text-foreground-500 mr-1.5"></i>
                  Centro de Operaciones — Métricas NOC
                </h2>
                <p className="text-foreground-400 text-[10px] md:text-xs mt-0.5">Monitoreo en tiempo real · Actualización cada 30s</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {nocKpis.map((kpi, idx) => (
                  <NocKpiCard key={kpi.id} kpi={kpi} index={idx} />
                ))}
              </div>
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 2 — ATENCIÓN INMEDIATA
                ════════════════════════════════════════════════ */}
            <section>
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 border-b border-background-200">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${dangerCount > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
                    <i className={`${dangerCount > 0 ? 'ri-error-warning-fill text-red-500' : 'ri-alert-fill text-amber-500'} text-sm`}></i>
                  </div>
                  <div>
                    <h2 className="text-foreground-950 font-semibold text-sm">Atención inmediata</h2>
                    <p className="text-foreground-400 text-[10px]">{attentionItems.length} asuntos requieren acción</p>
                  </div>
                </div>
                <div className="divide-y divide-background-200">
                  {attentionItems.length === 0 ? (
                    <div className="flex flex-col items-center text-center gap-2 py-10">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <i className="ri-check-double-line text-emerald-500 text-lg"></i>
                      </div>
                      <p className="text-foreground-800 text-xs font-semibold">Todo al día</p>
                      <p className="text-foreground-400 text-[10px]">No hay asuntos que requieran atención.</p>
                    </div>
                  ) : (
                    attentionItems.map((item) => (
                      <AttentionRow key={item.id} item={item} />
                    ))
                  )}
                </div>
              </div>
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 3 — CRECIMIENTO
                ════════════════════════════════════════════════ */}
            <section>
              <div className="mb-4">
                <h2 className="text-foreground-950 font-semibold text-sm md:text-base">Crecimiento</h2>
                <p className="text-foreground-400 text-[10px] md:text-xs mt-0.5">Métricas de expansión de la plataforma</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {growthMetrics.map((metric, idx) => (
                  <GrowthCard key={metric.id} metric={metric} index={idx} />
                ))}
              </div>
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 3.2 — NOC · PLATFORM HEALTH
                ════════════════════════════════════════════════ */}
            <section>
              <PlatformHealthPanel services={platformServices} />
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 3.5 — ECONOMY SCORE — Potencial Comercial
                ════════════════════════════════════════════════ */}
            <section>
              <div className="mb-4">
                <h2 className="text-foreground-950 font-semibold text-sm md:text-base">
                  <i className="ri-funds-line text-emerald-500 mr-1.5"></i>
                  Economy Score — Potencial Comercial
                </h2>
                <p className="text-foreground-400 text-[10px] md:text-xs mt-0.5">
                  Radios con mayor capacidad de monetización · Basado en 6 factores comerciales
                </p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-background-200">
                  {topEconomyRadios.slice(0, 6).map((radio, idx) => {
                    const ecoData = radioEconomyScores[radio.id];
                    const stConfig = economyStateConfig[radio.state];
                    return (
                      <div
                        key={radio.id}
                        className="p-4 flex items-center gap-4 hover:bg-background-100 transition-colors cursor-pointer"
                        onClick={async () => {
                          if (!ecoData) return;
                          // Will navigate to radios page
                          const { default: Swal } = await import('sweetalert2');
                          Swal.fire({
                            title: `Economy Score · ${radio.name}`,
                            html: `<div style="text-align:left;font-size:13px;">
                              <p style="color:#6b7280;font-size:11px;margin-bottom:12px;">
                                <strong>Frecuencia:</strong> ${radio.frequency}<br/>
                                <strong>Ciudad:</strong> ${radio.city}<br/>
                                <strong>Patrocinadores:</strong> ${ecoData.sponsorsCount} · <strong>Campañas:</strong> ${ecoData.activeCampaigns}
                              </p>
                              <p style="color:#6b7280;font-size:11px;">Ve al detalle de la radio para ver el desglose completo de los 6 factores.</p>
                            </div>`,
                            confirmButtonText: 'Ir a Radios',
                            confirmButtonColor: '#1f2937',
                            showCancelButton: true,
                            cancelButtonText: 'Cerrar',
                            cancelButtonColor: '#d1d5db',
                            customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
                            buttonsStyling: false,
                          }).then((r) => { if (r.isConfirmed) navigate('/sa/radios'); });
                        }}
                      >
                        <RadioEconomyScore
                          score={radio.score}
                          factors={ecoData?.factors ?? { seguidores: 0, oyentes: 0, interaccion: 0, patrocinadores: 0, campañas: 0, eventos: 0 }}
                          size="sm"
                          showLabel={false}
                          clickable={false}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground-800 text-xs font-semibold truncate">{radio.name}</p>
                          <p className="text-foreground-400 text-[10px]">{radio.frequency} · {radio.city}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${stConfig.color} ${stConfig.ring.replace('text-', 'bg-')}/15`}>
                              <span className={`w-1 h-1 rounded-full ${stConfig.dot}`}></span>
                              {stConfig.label}
                            </span>
                            {ecoData && (
                              <span className="text-foreground-300 text-[9px]">{ecoData.sponsorsCount} patrocinador{ecoData.sponsorsCount !== 1 ? 'es' : ''} · {ecoData.activeCampaigns} campaña{ecoData.activeCampaigns !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-foreground-200 text-[9px] font-bold shrink-0">#{idx + 1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 3.6 — NOC · ALERTAS CRÍTICAS
                ════════════════════════════════════════════════ */}
            <section>
              <NocCriticalAlerts alerts={nocCriticalAlerts} />
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 4 — ACTIVIDAD (Timeline Facebook)
                ════════════════════════════════════════════════ */}
            <section>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-foreground-950 font-semibold text-sm">Actividad</h2>
                    <p className="text-foreground-400 text-[10px] mt-0.5">Lo que está pasando en la plataforma</p>
                  </div>
                  {hasMoreTimeline && (
                    <button
                      onClick={() => setTimelineExpanded(!timelineExpanded)}
                      className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      {timelineExpanded ? 'Ver menos' : `Ver todo (${timeline.length})`}
                    </button>
                  )}
                </div>

                {timeline.length === 0 ? (
                  <div className="flex flex-col items-center text-center gap-2 py-8">
                    <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
                      <i className="ri-history-line text-foreground-400 text-lg"></i>
                    </div>
                    <p className="text-foreground-800 text-xs font-semibold">Sin actividad reciente</p>
                    <p className="text-foreground-400 text-[10px]">Los eventos nuevos van a aparecer acá.</p>
                  </div>
                ) : (
                  <div>
                    {displayedTimeline.map((event, idx) => (
                      <TimelineItem key={event.id} event={event} isLast={idx === displayedTimeline.length - 1} />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 5 — NOC · ACTIVIDAD RECIENTE
                ════════════════════════════════════════════════ */}
            <section>
              <NocActivityFeed activities={nocActivities} />
            </section>

            {/* ════════════════════════════════════════════════
                SECCIÓN 6 — NOC · QUICK ACTIONS
                ════════════════════════════════════════════════ */}
            <section>
              <QuickActionsPanel actions={quickActions} />
            </section>

          </div>
        </main>
      </div>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-2.5 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · RadioFrecuenciaWeb
        </p>
      </div>
    </div>
  );
}